import {useCallback, useEffect, useMemo, useState} from 'react';
import {Alert, Button, Form, Spinner} from 'react-bootstrap';
import {chatErrorMessage, userDisplayName} from './chatUtils';

const TYPES = [
    {value: 'CHANNEL', label: 'Channel'},
    {value: 'DIRECT', label: 'Direct message'},
    {value: 'GROUP_DM', label: 'Group message'},
];

export default function ConversationModal({show, onHide, onCreated, chat}) {
    const [kind, setKind] = useState('CHANNEL');
    const [name, setName] = useState('');
    const [selected, setSelected] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const [directoryUsers, setDirectoryUsers] = useState([]);
    const [directoryCursor, setDirectoryCursor] = useState('');
    const [directoryHasMore, setDirectoryHasMore] = useState(false);
    const [directoryLoading, setDirectoryLoading] = useState(false);
    const {refreshDirectory, refreshAdminDirectory, searchDirectory} = chat;
    const peers = useMemo(() => directoryUsers.filter(user => user.id !== chat.currentUser?.id), [directoryUsers, chat.currentUser?.id]);
    const adminUsers = chat.adminUsers || chat.users;

    const loadDirectory = useCallback(async ({cursor = '', signal} = {}) => {
        setDirectoryLoading(true);
        try {
            const payload = searchDirectory
                ? await searchDirectory({query, cursor, signal})
                : await refreshDirectory(signal, {query, cursor});
            setDirectoryUsers(current => cursor
                ? [...new Map([...current, ...(payload.users || [])].map(user => [user.id, user])).values()]
                : (payload.users || []));
            setDirectoryCursor(payload.next_cursor || '');
            setDirectoryHasMore(Boolean(payload.has_more));
            return payload;
        } finally {
            if (!signal?.aborted) setDirectoryLoading(false);
        }
    }, [query, refreshDirectory, searchDirectory]);

    useEffect(() => {
        if (!show) return;
        setKind('CHANNEL');
        setName('');
        setSelected([]);
        setQuery('');
        setDirectoryUsers([]);
        setDirectoryCursor('');
        setDirectoryHasMore(false);
        setError(null);
        const controller = new AbortController();
        const loadAdmins = refreshAdminDirectory || refreshDirectory;
        Promise.all([
            loadAdmins?.(controller.signal),
            searchDirectory
                ? searchDirectory({query: '', signal: controller.signal})
                : refreshDirectory(controller.signal),
        ]).then(([, payload]) => {
            if (!controller.signal.aborted) {
                setDirectoryUsers(payload?.users || []);
                setDirectoryCursor(payload?.next_cursor || '');
                setDirectoryHasMore(Boolean(payload?.has_more));
            }
        }).catch(loadError => {
            if (loadError?.name !== 'AbortError') setError(loadError);
        });
        return () => controller.abort();
    }, [refreshAdminDirectory, refreshDirectory, searchDirectory, show]);

    useEffect(() => {
        if (!show || kind === 'CHANNEL') return undefined;
        const controller = new AbortController();
        const timer = window.setTimeout(() => {
            loadDirectory({signal: controller.signal}).catch(loadError => {
                if (loadError?.name !== 'AbortError') setError(loadError);
            });
        }, query ? 250 : 0);
        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [kind, loadDirectory, query, show]);

    function toggleUser(userId) {
        if (kind === 'DIRECT') {
            setSelected([userId]);
            return;
        }
        setSelected(current => current.includes(userId)
            ? current.filter(id => id !== userId)
            : [...current, userId]);
    }

    async function submit(event) {
        event.preventDefault();
        setError(null);
        if (!chat.currentUser) return;
        if (kind === 'CHANNEL' && !name.trim()) {
            setError(new Error('Channel name is required.'));
            return;
        }
        if (kind === 'DIRECT' && selected.length !== 1) {
            setError(new Error('Choose one person for a direct message.'));
            return;
        }
        if (kind === 'GROUP_DM' && selected.length < 2) {
            setError(new Error('Choose at least two other people for a group message.'));
            return;
        }
        setSubmitting(true);
        try {
            let memberUserIds;
            if (kind === 'CHANNEL') {
                const directory = await (refreshAdminDirectory || refreshDirectory)();
                memberUserIds = (directory.users || directory.admin_users || adminUsers).map(user => user.id);
            } else {
                memberUserIds = [chat.currentUser.id, ...selected];
            }
            const response = await chat.createConversation({
                kind,
                name: kind === 'CHANNEL' ? name.trim() : undefined,
                member_user_ids: memberUserIds,
                access_policy_code: 'ADMIN_MANAGED',
            });
            onCreated(response.conversation, response.reused);
            onHide();
        } catch (submitError) {
            setError(submitError);
            if (submitError.code === 'INACTIVE_MEMBER') loadDirectory().catch(() => {});
        } finally {
            setSubmitting(false);
        }
    }

    if (!show) return null;
    return (
        <section className="chat-management-pane p-4" aria-label="New conversation">
            <Form onSubmit={submit}>
                <header>
                    <h1>New conversation</h1>
                </header>
                <div>
                    {error ? <Alert variant="danger">{chatErrorMessage(error)}</Alert> : null}
                    <div className="chat-kind-picker" role="group" aria-label="Conversation type">
                        {TYPES.filter(type => type.value !== 'CHANNEL' || chat.currentUser?.role === 'ADMIN').map(type => (
                            <Button
                                key={type.value}
                                type="button"
                                variant={kind === type.value ? 'primary' : 'outline-secondary'}
                                onClick={() => {
                                    setKind(type.value);
                                    setSelected([]);
                                    setError(null);
                                }}
                            >
                                {type.label}
                            </Button>
                        ))}
                    </div>
                    {kind === 'CHANNEL' ? (
                        <>
                            <Form.Group className="mt-4" controlId="chat-channel-name">
                                <Form.Label>Channel name</Form.Label>
                                <Form.Control
                                    autoFocus
                                    value={name}
                                    maxLength={120}
                                    placeholder="e.g. operations"
                                    onChange={event => setName(event.target.value)}
                                />
                            </Form.Group>
                            <p className="text-muted mt-3 mb-0">
                                All {adminUsers.length} active Admins will be able to read and post in this channel.
                            </p>
                        </>
                    ) : (
                        <>
                            <Form.Group className="mt-4" controlId="chat-member-search">
                                <Form.Label>Search people</Form.Label>
                                <Form.Control
                                    value={query}
                                    placeholder="Name or username"
                                    onChange={event => setQuery(event.target.value)}
                                />
                            </Form.Group>
                            <div className="chat-admin-picker mt-3" aria-label="Chat members">
                                <div className="chat-admin-picker-title">
                                    {kind === 'DIRECT' ? 'Choose a person' : 'Choose at least two people'}
                                </div>
                                {!directoryLoading && peers.length === 0 ? <p className="text-muted mt-3">No matching active Chat users are available.</p> : null}
                                {peers.map(user => (
                                    <Form.Check
                                        key={user.id}
                                        type={kind === 'DIRECT' ? 'radio' : 'checkbox'}
                                        name={kind === 'DIRECT' ? 'direct-admin' : undefined}
                                        id={`chat-user-${user.id}`}
                                        checked={selected.includes(user.id)}
                                        onChange={() => toggleUser(user.id)}
                                        label={<span><strong>{userDisplayName(user)}</strong> <span className="text-muted">@{user.username} · {user.role || 'ADMIN'}</span></span>}
                                    />
                                ))}
                                {directoryLoading ? <div className="text-muted mt-3"><Spinner size="sm" className="me-2" />Loading people…</div> : null}
                                {directoryHasMore && !directoryLoading ? (
                                    <Button type="button" variant="link" onClick={() => loadDirectory({cursor: directoryCursor}).catch(setError)}>
                                        Load more people
                                    </Button>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>
                <footer className="wg-savebar">
                    <Button type="button" variant="outline-secondary" onClick={onHide}>Cancel</Button>
                    <Button type="submit" disabled={submitting || !chat.currentUser}>
                        {submitting ? <><Spinner size="sm" className="me-2" />Creating…</> : 'Create'}
                    </Button>
                </footer>
            </Form>
        </section>
    );
}
